import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Input,
  Text,
  Spacer,
  Stack,
  FormRow
} from '@efast_public/fjd-component-library';
import { useNotification } from 'shared/notification/useNotification';
import { RouteKey } from '../AppRoutes';
import { GovosApiRoutes, govosClient } from '../../api/govos-api';

interface ResetPasswordRequest {
  email: string;
}

interface ResetPasswordFormData {
  email: string;
}

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const { notifyUserFromRequest } = useNotification();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<ResetPasswordFormData>({
    mode: 'onBlur',
    reValidateMode: 'onChange'
  });

  const { mutate: resetPasswordMutation, isPending } = useMutation({
    mutationFn: async (request: ResetPasswordRequest) =>
      notifyUserFromRequest(
        () => govosClient.post(GovosApiRoutes.resetPassword(), request),
        {
          loadingMessage: t('LoginPage.resetPasswordLoading'),
          successMessage: t('LoginPage.resetPasswordSuccess'),
          errorMessage: t('LoginPage.resetPasswordError')
        }
      ),
    onSuccess: () => {
      navigate(RouteKey.RESET_PASSWORD_SUCCESS);
    }
  });

  const onSubmit = (data: ResetPasswordFormData) => {
    resetPasswordMutation({ email: data.email });
  };

  return (
    <div style={{ maxWidth: '70%', margin: '0 auto', padding: '40px 20px' }}>
      <Stack direction="column" spacing="l">
        <Stack direction="column">
          <Text size="xxl" strong style={{ marginBottom: '8px' }}>
            {t('LoginPage.resetPasswordTitle')}
          </Text>
          <Text size="m">{t('LoginPage.resetPasswordSubtitle')}</Text>
        </Stack>

        <Spacer size="l" />

        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack direction="column" spacing="m">
            <div>
              <FormRow
                id="email"
                label={t('LoginPage.emailLabel')}
                variant="error"
                validationMessage={errors.email?.message}
              >
                <Input
                  data-testid="reset-password-email-input"
                  type="email"
                  placeholder=""
                  style={{ width: '100%', maxWidth: '350px' }}
                  {...register('email', {
                    required: t('LoginPage.emailRequired'),
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: t('LoginPage.emailInvalid')
                    }
                  })}
                />
              </FormRow>
            </div>

            <div
              style={{
                display: 'flex',
                gap: '16px',
                alignItems: 'center',
                marginTop: '8px'
              }}
            >
              <Button
                data-testid="back-to-login"
                type="button"
                variant="primary-text"
                onClick={() => navigate(RouteKey.LOGIN)}
              >
                {t('LoginPage.backToLogin')}
              </Button>
              <Button
                data-testid="reset-password-button"
                type="submit"
                variant="primary"
                disabled={isPending}
              >
                {isPending
                  ? t('LoginPage.resetting')
                  : t('LoginPage.resetPasswordButton')}
              </Button>
            </div>
          </Stack>
        </form>
      </Stack>
    </div>
  );
}
